import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { type FormEvent, useEffect, useState } from "react";

type PropertyFormState = {
  property_name: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  county: string;
  postcode: string;
  total_rooms: string;
};

type PropertyModalInitialValue = {
  id?: number;
  property_name?: string;
  address_line_1?: string;
  address_line_2?: string | null;
  city?: string;
  county?: string | null;
  postcode?: string;
  total_rooms?: string | number | null;
};

type PropertyModalProps = {
  open: boolean;
  mode: "create" | "edit";
  initialValue?: PropertyModalInitialValue;
  onClose: () => void;
  onSubmit: (payload: Record<string, unknown>) => Promise<void>;
};

const emptyForm = (): PropertyFormState => ({
  property_name: "",
  address_line_1: "",
  address_line_2: "",
  city: "",
  county: "",
  postcode: "",
  total_rooms: "1",
});

export function PropertyModal({
  open,
  mode,
  initialValue,
  onClose,
  onSubmit,
}: PropertyModalProps) {
  const [formState, setFormState] = useState<PropertyFormState>(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setFormState({
        property_name: initialValue?.property_name ?? "",
        address_line_1: initialValue?.address_line_1 ?? "",
        address_line_2: initialValue?.address_line_2 ?? "",
        city: initialValue?.city ?? "",
        county: initialValue?.county ?? "",
        postcode: initialValue?.postcode ?? "",
        total_rooms: initialValue?.total_rooms?.toString() ?? "1",
      });
    }
  }, [open, initialValue]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      await onSubmit({
        id: initialValue?.id,
        property_name: formState.property_name,
        address_line_1: formState.address_line_1,
        address_line_2: formState.address_line_2 || null,
        city: formState.city,
        county: formState.county || null,
        postcode: formState.postcode,
        total_rooms: Number(formState.total_rooms),
      });
      onClose();
      setFormState(emptyForm());
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 6 } }}
    >
      <Box component="form" onSubmit={handleSubmit}>
        <DialogTitle>
          {mode === "edit" ? "Edit property" : "Add property"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {mode === "edit"
              ? "Adjust the property details and keep the record current."
              : "Create a property record with the address and occupancy details from the schema."}
          </DialogContentText>
          <Stack spacing={2.25} sx={{ mt: 1 }}>
            <TextField
              label="Property name"
              value={formState.property_name}
              onChange={(event) =>
                setFormState((current) => ({
                  ...current,
                  property_name: event.target.value,
                }))
              }
              required
              fullWidth
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6 } }}
            />
            <TextField
              label="Address line 1"
              value={formState.address_line_1}
              onChange={(event) =>
                setFormState((current) => ({
                  ...current,
                  address_line_1: event.target.value,
                }))
              }
              required
              fullWidth
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6 } }}
            />
            <TextField
              label="Address line 2"
              value={formState.address_line_2}
              onChange={(event) =>
                setFormState((current) => ({
                  ...current,
                  address_line_2: event.target.value,
                }))
              }
              fullWidth
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6 } }}
            />
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                },
              }}
            >
              <TextField
                label="City"
                value={formState.city}
                onChange={(event) =>
                  setFormState((current) => ({
                    ...current,
                    city: event.target.value,
                  }))
                }
                required
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6 } }}
              />
              <TextField
                label="County"
                value={formState.county}
                onChange={(event) =>
                  setFormState((current) => ({
                    ...current,
                    county: event.target.value,
                  }))
                }
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6 } }}
              />
            </Box>
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                },
              }}
            >
              <TextField
                label="Postcode"
                value={formState.postcode}
                onChange={(event) =>
                  setFormState((current) => ({
                    ...current,
                    postcode: event.target.value,
                  }))
                }
                required
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6 } }}
              />
              <TextField
                label="Total rooms"
                type="number"
                inputProps={{ min: 1 }}
                value={formState.total_rooms}
                onChange={(event) =>
                  setFormState((current) => ({
                    ...current,
                    total_rooms: event.target.value,
                  }))
                }
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6 } }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} color="inherit" sx={{ borderRadius: 6 }}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disableElevation
            disabled={isSubmitting}
            sx={{ borderRadius: 6 }}
          >
            {isSubmitting
              ? "Saving..."
              : mode === "edit"
                ? "Update property"
                : "Create property"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
